import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/context/AuthContext';
import {
  Search, Eye, ChevronLeft, ChevronRight, RefreshCw,
  FileText, Clock, CheckCircle, XCircle
} from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';

const STATUS_COLORS = {
  new: { bg: 'bg-blue-100', text: 'text-blue-700', icon: Clock, label: 'New' },
  callback: { bg: 'bg-orange-100', text: 'text-orange-700', icon: Clock, label: 'Callback' },
  reviewing: { bg: 'bg-yellow-100', text: 'text-yellow-700', icon: FileText, label: 'Reviewing' },
  approved: { bg: 'bg-green-100', text: 'text-green-700', icon: CheckCircle, label: 'Approved' },
  rejected: { bg: 'bg-red-100', text: 'text-red-700', icon: XCircle, label: 'Rejected' },
};

const SERVICE_LABELS = {
  loan: 'Payday Loan',
  vehicle: 'Vehicle Finance',
  insurance: 'Insurance',
  bank: 'Diamond Card',
  home: 'Home Loan',
};

export const AdminApplications = () => {
  const { adminApi } = useAuth();
  const [apps, setApps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [viewApp, setViewApp] = useState(null);
  const [viewDetail, setViewDetail] = useState(null);

  const fetchApps = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: page.toString(), per_page: '25', search,
        ...(statusFilter && { status: statusFilter }),
      });
      const res = await adminApi.get(`/admin/applications?${params}`);
      setApps(res.data.applications);
      setTotal(res.data.total);
      setTotalPages(res.data.total_pages);
    } catch { toast.error('Failed to load applications'); }
    finally { setLoading(false); }
  }, [adminApi, page, search, statusFilter]);

  useEffect(() => { fetchApps(); }, [fetchApps]);

  const handleSearch = (e) => { e.preventDefault(); setPage(1); setSearch(searchInput); };

  const handleViewDetail = async (id) => {
    try {
      const res = await adminApi.get(`/admin/applications/${id}`);
      setViewDetail(res.data);
      setViewApp(true);
    } catch { toast.error('Failed to load application'); }
  };

  const updateStatus = async (id, newStatus) => {
    try {
      await adminApi.put(`/admin/applications/${id}/status`, { status: newStatus });
      toast.success(`Application ${newStatus}`);
      setViewApp(null);
      setViewDetail(null);
      fetchApps();
    } catch { toast.error('Failed to update status'); }
  };

  const renderSection = (title, data) => {
    if (!data || Object.keys(data).length === 0) return null;
    return (
      <div className="mt-4">
        <h4 className="font-syne font-bold text-gray-900 text-sm mb-2">{title}</h4>
        <div className="grid grid-cols-2 gap-2">
          {Object.entries(data).map(([k, v]) => (
            <div key={k} className="bg-gray-50 rounded-lg p-2">
              <p className="text-gray-400 text-[10px] uppercase">{k.replace(/_/g, ' ')}</p>
              <p className="text-gray-900 text-sm">{v || '-'}</p>
            </div>
          ))}
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-5" data-testid="admin-applications">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-syne text-2xl font-bold text-gray-900">New Applications</h1>
          <p className="text-gray-500 text-sm">{total} total applications</p>
        </div>
        <Button onClick={fetchApps} variant="outline" size="sm" className="border-gray-300 text-gray-600" data-testid="refresh-apps-btn">
          <RefreshCw className="w-4 h-4 mr-1" /> Refresh
        </Button>
      </div>

      <div className="flex gap-3 flex-wrap">
        <form onSubmit={handleSearch} className="flex-1 min-w-[240px] relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <Input value={searchInput} onChange={(e) => setSearchInput(e.target.value)} placeholder="Search name, reference, phone..." className="pl-10 bg-white border-gray-300 text-gray-900" data-testid="search-apps-input" />
        </form>
        <select value={statusFilter} onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }} className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900" data-testid="filter-status">
          <option value="">All Statuses</option>
          <option value="new">New</option>
          <option value="callback">Callback</option>
          <option value="reviewing">Reviewing</option>
          <option value="approved">Approved</option>
          <option value="rejected">Rejected</option>
        </select>
      </div>

      <Card className="bg-white border border-gray-200 rounded-xl overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center h-48">
            <div className="w-8 h-8 border-2 border-afriperx-gold border-t-transparent rounded-full animate-spin" />
          </div>
        ) : apps.length === 0 ? (
          <div className="text-center py-16">
            <FileText className="w-12 h-12 text-gray-300 mx-auto mb-3" />
            <p className="text-gray-400">No applications yet</p>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full" data-testid="applications-table">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Reference</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Applicant</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Phone</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Services</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Status</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Date</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Action</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {apps.map(a => {
                    const st = STATUS_COLORS[a.status] || STATUS_COLORS.new;
                    return (
                      <tr key={a.id} className="hover:bg-gray-50 transition-colors">
                        <td className="px-4 py-3 text-sm font-mono text-gray-900">{a.reference}</td>
                        <td className="px-4 py-3 text-sm text-gray-700">{a.full_name}</td>
                        <td className="px-4 py-3 text-sm text-gray-500">{a.cell_number || '-'}</td>
                        <td className="px-4 py-3">
                          <div className="flex gap-1 flex-wrap">
                            {a.services?.map(s => (
                              <span key={s} className="text-[10px] bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full">{SERVICE_LABELS[s] || s}</span>
                            ))}
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <span className={`text-xs font-medium px-2 py-1 rounded-full ${st.bg} ${st.text}`}>{st.label}</span>
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-400">{a.created_at ? new Date(a.created_at).toLocaleDateString() : '-'}</td>
                        <td className="px-4 py-3">
                          <Button onClick={() => handleViewDetail(a.id)} variant="ghost" size="sm" className="text-blue-600 hover:bg-blue-50" data-testid={`view-app-${a.id}`}>
                            <Eye className="w-4 h-4" />
                          </Button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <div className="flex items-center justify-between px-4 py-3 border-t border-gray-200 bg-gray-50">
              <p className="text-sm text-gray-500">Page {page} of {totalPages}</p>
              <div className="flex gap-2">
                <Button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page <= 1} variant="outline" size="sm" className="border-gray-300"><ChevronLeft className="w-4 h-4" /></Button>
                <Button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page >= totalPages} variant="outline" size="sm" className="border-gray-300"><ChevronRight className="w-4 h-4" /></Button>
              </div>
            </div>
          </>
        )}
      </Card>

      {/* Detail Dialog */}
      <Dialog open={!!viewApp} onOpenChange={() => { setViewApp(null); setViewDetail(null); }}>
        <DialogContent className="bg-white border-gray-200 max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-gray-900 font-syne flex items-center gap-2">
              Application {viewDetail?.reference}
              {viewDetail && <span className={`text-xs font-medium px-2 py-1 rounded-full ${STATUS_COLORS[viewDetail.status]?.bg} ${STATUS_COLORS[viewDetail.status]?.text}`}>{STATUS_COLORS[viewDetail.status]?.label}</span>}
            </DialogTitle>
          </DialogHeader>
          {viewDetail && (
            <div className="space-y-2">
              <div className="flex gap-1 flex-wrap mb-3">
                {viewDetail.services?.map(s => (
                  <span key={s} className="text-xs bg-afriperx-green/10 text-afriperx-gold px-3 py-1 rounded-full font-medium">{SERVICE_LABELS[s] || s}</span>
                ))}
              </div>
              <div className="grid grid-cols-2 gap-2">
                {[
                  ['Full Name', viewDetail.full_name],
                  ['ID Number', viewDetail.id_number],
                  ['DOB', viewDetail.dob],
                  ['Gender', viewDetail.gender],
                  ['Marital Status', viewDetail.marital_status],
                  ['Nationality', viewDetail.nationality],
                  ['Cell', viewDetail.cell_number],
                  ['Email', viewDetail.email],
                  ['Address', viewDetail.address],
                  ['City', viewDetail.city],
                  ['Postal Code', viewDetail.postal_code],
                  ['Employment', viewDetail.employment_status],
                  ['Employer', viewDetail.employer],
                  ['Gross Income', viewDetail.gross_income],
                  ['Net Income', viewDetail.net_income],
                ].map(([label, val]) => (
                  <div key={label} className="bg-gray-50 rounded-lg p-2">
                    <p className="text-gray-400 text-[10px] uppercase">{label}</p>
                    <p className="text-gray-900 text-sm">{val || '-'}</p>
                  </div>
                ))}
              </div>
              {renderSection('Payday Loan Details', viewDetail.loan_data)}
              {renderSection('Vehicle Finance Details', viewDetail.vehicle_data)}
              {renderSection('Insurance Details', viewDetail.insurance_data)}
              {renderSection('Diamond Card Details', viewDetail.bank_card_data)}
              {renderSection('Home Loan Details', viewDetail.home_loan_data)}
              {viewDetail.status === 'new' && (
                <div className="flex gap-3 mt-4 pt-4 border-t border-gray-200">
                  <Button onClick={() => updateStatus(viewDetail.id, 'reviewing')} className="flex-1 bg-yellow-500 hover:bg-yellow-600 text-white" data-testid="mark-reviewing-btn">Mark as Reviewing</Button>
                  <Button onClick={() => updateStatus(viewDetail.id, 'approved')} className="flex-1 bg-green-600 hover:bg-green-700 text-white" data-testid="approve-app-btn">Approve</Button>
                  <Button onClick={() => updateStatus(viewDetail.id, 'rejected')} variant="outline" className="flex-1 border-red-300 text-red-600" data-testid="reject-app-btn">Reject</Button>
                </div>
              )}
              {viewDetail.status === 'reviewing' && (
                <div className="flex gap-3 mt-4 pt-4 border-t border-gray-200">
                  <Button onClick={() => updateStatus(viewDetail.id, 'approved')} className="flex-1 bg-green-600 hover:bg-green-700 text-white">Approve</Button>
                  <Button onClick={() => updateStatus(viewDetail.id, 'rejected')} variant="outline" className="flex-1 border-red-300 text-red-600">Reject</Button>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};
